import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, TrendingUp } from "lucide-react";
import { MaskedLine, FadeIn, SectionLabel } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { IMG, SERVICES, CASE_STUDIES, TESTIMONIALS, STATS } from "@/lib/data";

const ClockRing = () => (
  <div className="pointer-events-none absolute -top-16 -right-16 sm:top-8 sm:right-4 lg:right-24 z-0 opacity-90">
    <div className="relative h-56 w-56 sm:h-80 sm:w-80 animate-spin-slow">
      <svg viewBox="0 0 100 100" className="h-full w-full">
        <circle cx="50" cy="50" r="46" fill="none" stroke="#0062FF" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="2 4" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          const x1 = 50 + 40 * Math.sin(a);
          const y1 = 50 - 40 * Math.cos(a);
          const x2 = 50 + 44 * Math.sin(a);
          const y2 = 50 - 44 * Math.cos(a);
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0062FF" strokeWidth={i % 3 === 0 ? 2.5 : 1} strokeLinecap="round" />
          );
        })}
      </svg>
      <ArrowUpRight className="absolute inset-0 m-auto h-16 w-16 sm:h-24 sm:w-24 text-bm-blue" strokeWidth={2.5} />
    </div>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden bg-white pt-32 pb-20 lg:pt-40 lg:pb-28 grain">
      <ClockRing />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-bm-blue/30 bg-bm-blue/5 px-5 py-2 font-manrope text-xs font-bold uppercase tracking-[0.25em] text-bm-blue"
        >
          <span className="h-2 w-2 rounded-full bg-bm-blue animate-pulse" />
          Digital Marketing & Advertising Agency
        </motion.p>

        <motion.h1 style={{ y: textY }} className="font-syne font-extrabold tracking-tighter leading-[0.95] text-[10.5vw] sm:text-[11vw] lg:text-[8.5rem]">
          <MaskedLine delay={0.15}>CREATIVE</MaskedLine>
          <MaskedLine delay={0.28}>
            <span className="text-bm-blue">THINKING,</span>
          </MaskedLine>
          <MaskedLine delay={0.41}>AROUND THE</MaskedLine>
          <MaskedLine delay={0.54}>
            <span className="text-stroke-blue">CLOCK.</span>
          </MaskedLine>
        </motion.h1>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="font-manrope text-base sm:text-lg leading-relaxed text-bm-gray max-w-md">
              From SEO to AI video, ads to events — Brand Mitra 24 is the
              full-stack growth partner that never sleeps. Eleven services, one
              obsessed team, zero excuses.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-start-project-button"
                className="group inline-flex items-center gap-2 rounded-full bg-bm-blue px-8 py-4 font-manrope text-sm font-bold text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-12px_rgba(0,98,255,0.6)]"
              >
                Start a Project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-explore-services-button"
                className="group inline-flex items-center gap-2 rounded-full border-2 border-bm-ink px-8 py-4 font-manrope text-sm font-bold text-bm-ink transition-colors duration-300 hover:bg-bm-ink hover:text-white"
              >
                Explore Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="lg:col-span-7"
          >
            <div className="relative overflow-hidden rounded-2xl border border-bm-line shadow-[0_40px_80px_-30px_rgba(11,27,61,0.35)]">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src={IMG.heroAbstract}
                alt="Abstract blue clock — Brand Mitra 24"
                className="h-72 sm:h-96 w-full object-cover will-change-transform"
              />
              <div className="absolute bottom-4 left-4 rounded-full bg-white/90 backdrop-blur px-5 py-2 font-syne text-sm font-bold text-bm-navy">
                24 / 7 — ALWAYS ON
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesBento = () => {
  const featured = SERVICES.filter((s) =>
    ["seo", "paid-ads", "social-media-marketing", "web-design", "ai-video-generation", "branding"].includes(s.slug)
  );
  const spans = ["md:col-span-2", "", "", "", "", "md:col-span-2"];
  return (
    <section data-testid="home-services-bento" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <FadeIn className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <SectionLabel>What we do</SectionLabel>
            <h2 className="mt-4 font-syne text-4xl sm:text-5xl font-bold tracking-tight text-bm-ink">
              One roof. <span className="text-bm-blue">Eleven weapons.</span>
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="bento-view-all-link"
            className="group inline-flex items-center gap-2 font-manrope text-sm font-bold text-bm-ink"
          >
            View all services
            <ArrowUpRight className="h-4 w-4 text-bm-blue transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {featured.map((s, i) => (
            <FadeIn key={s.slug} delay={i * 0.08} className={spans[i]}>
              <Link
                to="/services"
                data-testid={`bento-card-${s.slug}`}
                className="group relative block h-72 overflow-hidden rounded-xl border border-bm-line"
              >
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bm-navy/90 via-bm-navy/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between">
                  <div>
                    <h3 className="font-syne text-2xl font-bold text-white">{s.title}</h3>
                    <p className="mt-1 font-manrope text-xs text-white/70">{s.tags.join(" · ")}</p>
                  </div>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-bm-blue text-white transition-transform duration-500 group-hover:rotate-45">
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

const Stats = () => (
  <section data-testid="home-stats" className="bg-bm-navy py-20 lg:py-28 grain relative">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
      {STATS.map((s, i) => (
        <FadeIn key={s.label} delay={i * 0.1}>
          <div className="border-l-2 border-bm-blue pl-6">
            <p className="font-syne text-5xl lg:text-6xl font-extrabold tracking-tighter text-white">
              {s.value}
            </p>
            <p className="mt-2 font-manrope text-sm text-white/60">{s.label}</p>
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);

const CaseStudies = () => (
  <section data-testid="home-case-studies" className="bg-bm-surface py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="mb-14">
        <SectionLabel>Selected work</SectionLabel>
        <h2 className="mt-4 font-syne text-4xl sm:text-5xl font-bold tracking-tight text-bm-ink">
          Proof, not promises.
        </h2>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {CASE_STUDIES.map((c, i) => (
          <FadeIn key={c.client} delay={i * 0.12}>
            <article
              data-testid={`case-study-${i}`}
              className="group relative overflow-hidden rounded-xl border border-bm-line bg-white"
            >
              <div className="h-72 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.client}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <div className="flex gap-2 mb-3">
                  {c.tags.map((t) => (
                    <span key={t} className="rounded-full border border-bm-blue/30 px-3 py-1 font-manrope text-[11px] font-bold uppercase tracking-wider text-bm-blue">
                      {t}
                    </span>
                  ))}
                </div>
                <h3 className="font-syne text-xl font-bold text-bm-ink">{c.title}</h3>
                <p className="mt-1 font-manrope text-sm text-bm-gray">{c.client}</p>
                <p className="mt-4 inline-flex items-center gap-2 font-syne text-base font-bold text-bm-blue">
                  <TrendingUp className="h-4 w-4" /> {c.result}
                </p>
              </div>
            </article>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="home-testimonials" className="bg-white py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="mb-14">
        <SectionLabel>Word on the street</SectionLabel>
        <h2 className="mt-4 font-syne text-4xl sm:text-5xl font-bold tracking-tight text-bm-ink">
          Clients who don't sleep on us.
        </h2>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <FadeIn key={t.name} delay={i * 0.12}>
            <blockquote
              data-testid={`testimonial-${i}`}
              className="flex h-full flex-col justify-between rounded-xl border border-bm-line p-8 transition-colors duration-300 hover:border-bm-blue"
            >
              <p className="font-manrope text-base leading-relaxed text-bm-ink">
                <span className="font-syne text-4xl font-extrabold text-bm-blue leading-none">“</span>
                {t.quote}
              </p>
              <footer className="mt-8">
                <p className="font-syne text-sm font-bold text-bm-ink">{t.name}</p>
                <p className="font-manrope text-xs text-bm-gray">{t.role}</p>
              </footer>
            </blockquote>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <ServicesBento />
      <Stats />
      <CaseStudies />
      <Testimonials />
    </main>
  );
}
